import React from 'react';

const AgileTestQuadrantsGraph = ({ data, quadrantsImageRef }) => {
  if (!data) return null;

  const quadrantData = data;

  // --- Dimension settings ---
  const topRectY = 80;
  const quadrantWidth = 350;
  const quadrantHeight = 280;
  const bottomRectY = topRectY + quadrantHeight;

  // Text wrapping function with maximum width
  const wrapText = (text, maxWidth, fontSize) => {
    // Estimate characters per line based on font size and max width
    const charsPerLine = Math.floor(maxWidth / (fontSize * 0.6));
    
    if (text.length <= charsPerLine) return [text];
    
    const words = text.split(' ');
    const lines = [];
    let currentLine = words[0];
    
    for (let i = 1; i < words.length; i++) {
      if ((currentLine + ' ' + words[i]).length <= charsPerLine) {
        currentLine += ' ' + words[i];
      } else {
        lines.push(currentLine);
        currentLine = words[i];
      }
    }
    
    if (currentLine) lines.push(currentLine);
    return lines;
  };

  // Calculate item heights to avoid overlapping
  const calculateItemPositions = (items, quadrantY, titleY, maxTextWidth) => {
    const baseLineHeight = 20;
    const itemSpacing = 10; // Space between items
    let currentY = titleY + 40; // Start below the title
    
    return items.map(item => {
      const lines = wrapText(item, maxTextWidth, 16);
      const itemHeight = lines.length * baseLineHeight;
      const itemY = currentY;
      
      // Update for next item
      currentY += itemHeight + itemSpacing;
      
      return {
        text: item,
        lines: lines,
        y: itemY
      };
    });
  };

  return (
    <svg width="100%" height="750" viewBox="0 0 800 700" ref={quadrantsImageRef}>
      <text x="400" y="30" textAnchor="middle" fontSize="27" fontWeight="bold">
        Agile Testing Quadrants
      </text>

      {/* Labels */}
      <text x="400" y="60" textAnchor="middle" fontSize="22">
        Business Facing
      </text>
      <text x="400" y="690" textAnchor="middle" fontSize="22">
        Technology Facing
      </text>
      <text x="20" y="350" textAnchor="middle" fontSize="22" transform="rotate(-90 20,350)">
        Supporting the Team
      </text>
      <text x="780" y="350" textAnchor="middle" fontSize="22" transform="rotate(90 780,350)">
        Critique Product
      </text>

      {/* Quadrants */}
      <rect
        x="50"
        y={topRectY}
        width={quadrantWidth}
        height={quadrantHeight}
        fill={quadrantData.Q1.color}
      />
      <rect
        x="400"
        y={topRectY}
        width={quadrantWidth}
        height={quadrantHeight}
        fill={quadrantData.Q2.color}
      />
      <rect
        x="50"
        y={bottomRectY}
        width={quadrantWidth}
        height={quadrantHeight}
        fill={quadrantData.Q3.color}
      />
      <rect
        x="400"
        y={bottomRectY}
        width={quadrantWidth}
        height={quadrantHeight}
        fill={quadrantData.Q4.color}
      />

      {/* Quadrant content with better spacing */}
      {Object.entries(quadrantData).map(([quadrant, info]) => {
        // Calculate quadrant positions
        const quadX = quadrant === 'Q1' || quadrant === 'Q3' ? 50 : 400;
        const quadY = quadrant === 'Q1' || quadrant === 'Q2' ? topRectY : bottomRectY;
        
        // Center of quadrant for text alignment
        const centerX = quadX + quadrantWidth / 2;
        
        // Title position
        const titleY = quadY + 40;
        
        // Calculate item positions with proper spacing
        const maxTextWidth = quadrantWidth - 60; // Padding on both sides
        const itemPositions = calculateItemPositions(info.items, quadY, titleY, maxTextWidth);
        
        return (
          <g key={quadrant}>
            {/* Quadrant title */}
            <text
              x={centerX}
              y={titleY}
              textAnchor="middle"
              fontSize="18"
              fontWeight="bold"
            >
              {info.title}
            </text>
            
            {/* Quadrant items with better spacing */}
            {itemPositions.map((item, itemIndex) => (
              <g key={`item-${itemIndex}`}>
                {item.lines.map((line, lineIndex) => (
                  <text
                    key={`line-${itemIndex}-${lineIndex}`}
                    x={centerX}
                    y={item.y + (lineIndex * 20)}
                    textAnchor="middle"
                    fontSize="16"
                  >
                    {line}
                  </text>
                ))}
              </g>
            ))}
          </g>
        );
      })}

      {/* Automation circles */}
      <circle cx="50" cy={topRectY} r="50" fill="#FFB3B3" />
      <circle cx="750" cy={topRectY} r="50" fill="#FFB3B3" />
      <circle cx="50" cy={bottomRectY + quadrantHeight} r="50" fill="#FFB3B3" />
      <circle cx="750" cy={bottomRectY + quadrantHeight} r="50" fill="#FFB3B3" />

      {/* Automation text */}
      <text x="50" y={topRectY - 5} textAnchor="middle" fontSize="15">
        {quadrantData.Q2.automation.includes('&') ? (
          <>
            {quadrantData.Q2.automation.split(' & ')[0]}
            <tspan x="50" dy="1.2em">
              &
            </tspan>
            <tspan x="50" dy="0.8em">
              {quadrantData.Q2.automation.split(' & ')[1]}
            </tspan>
          </>
        ) : (
          quadrantData.Q2.automation
        )}
      </text>

      <text x="750" y={topRectY + 5} textAnchor="middle" fontSize="15">
        {quadrantData.Q3.automation.includes('&') ? (
          <>
            {quadrantData.Q3.automation.split(' & ')[0]}
            <tspan x="750" dy="0.8em">
              &
            </tspan>
            <tspan x="750" dy="0.8em">
              {quadrantData.Q3.automation.split(' & ')[1]}
            </tspan>
          </>
        ) : (
          quadrantData.Q3.automation
        )}
      </text>
      <text x="50" y={bottomRectY + quadrantHeight + 5} textAnchor="middle" fontSize="15">
        {quadrantData.Q1.automation.includes('&') ? (
          <>
            {quadrantData.Q1.automation.split(' & ')[0]}
            <tspan x="50" dy="0.9em">
              &
            </tspan>
            <tspan x="50" dy="0.8em">
              {quadrantData.Q1.automation.split(' & ')[1]}
            </tspan>
          </>
        ) : (
          quadrantData.Q1.automation
        )}
      </text>
      <text x="750" y={bottomRectY + quadrantHeight + 5} textAnchor="middle" fontSize="15">
        {quadrantData.Q4.automation.includes(',') ? (
          <>
            {quadrantData.Q4.automation.split(',').map((word, index) => (
              <tspan key={index} x="750" dy={index === 0 ? 0 : '1.2em'}>
                {word.trim()}
              </tspan>
            ))}
          </>
        ) : (
          quadrantData.Q4.automation
        )}
      </text>
    </svg>
  );
};

export default AgileTestQuadrantsGraph;